import React, { useState } from 'react';

// Eye icons for password visibility toggle
const EyeIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
  </svg>
);

const GoogleIcon: React.FC = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s12-5.373 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-2.641-.21-5.236-.611-7.743z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 44 30.038 44 24c0-2.641-.21-5.236-.611-7.743z" />
  </svg>
);

export interface Testimonial {
  avatarSrc: string;
  name: string;
  handle: string;
  text: string;
}

interface SignInComponentProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  heroImageSrc?: string;
  testimonials?: Testimonial[];
  onSignIn?: (event: React.FormEvent<HTMLFormElement>) => void;
  onGoogleSignIn?: () => void;
  onResetPassword?: () => void;
  onCreateAccount?: () => void;
  showSellerLink?: boolean;
  onSellerLinkClick?: () => void;
}

const GlassInputWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-2xl border border-gray-300 bg-white/10 backdrop-blur-sm transition-colors focus-within:border-violet-400/70 focus-within:bg-violet-500/10">
    {children}
  </div>
);

const TestimonialCard: React.FC<{ testimonial: Testimonial; delay: string }> = ({ testimonial, delay }) => (
  <div className={`animate-slide-up ${delay} flex items-start gap-3 rounded-3xl bg-white/20 backdrop-blur-xl border border-white/10 p-5 w-64`}>
    <img src={testimonial.avatarSrc} className="h-10 w-10 object-cover rounded-2xl" alt="avatar" />
    <div className="text-sm leading-snug">
      <p className="flex items-center gap-1 font-medium text-white">{testimonial.name}</p>
      <p className="text-white/70">{testimonial.handle}</p>
      <p className="mt-1 text-white/90">{testimonial.text}</p>
    </div>
  </div>
);

const SignInComponent: React.FC<SignInComponentProps> = ({
  title = <span className="font-light text-white tracking-tighter">Welcome</span>,
  description = "Access your account and continue your journey with us",
  heroImageSrc,
  testimonials = [],
  onSignIn,
  onGoogleSignIn,
  onResetPassword,
  onCreateAccount,
  showSellerLink = false,
  onSellerLinkClick,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-sans w-full">
      {/* Left column: sign-in form */}
      <section className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="flex flex-col gap-6">
            <h1 className="animate-fade-in text-4xl md:text-5xl font-semibold leading-tight text-gray-900">
              {typeof title === 'string' ? <span className="font-light tracking-tighter">{title}</span> : title}
            </h1>
            <p className="animate-fade-in-delay text-gray-600">{description}</p>

            <form className="space-y-5" onSubmit={onSignIn}>
              <div className="animate-fade-in-delay">
                <label className="text-sm font-medium text-gray-700">Email Address</label>
                <GlassInputWrapper>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    className="w-full bg-transparent text-sm p-4 rounded-2xl focus:outline-none text-gray-900 placeholder-gray-500"
                  />
                </GlassInputWrapper>
              </div>

              <div className="animate-fade-in-delay-2">
                <label className="text-sm font-medium text-gray-700">Password</label>
                <GlassInputWrapper>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className="w-full bg-transparent text-sm p-4 pr-12 rounded-2xl focus:outline-none text-gray-900 placeholder-gray-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center"
                    >
                      {showPassword ?
                        <EyeOffIcon /> :
                        <EyeIcon />
                      }
                    </button>
                  </div>
                </GlassInputWrapper>
              </div>

              <div className="animate-fade-in-delay-2 flex items-center justify-between text-sm">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" name="rememberMe" className="rounded" />
                  <span className="text-gray-700">Keep me signed in</span>
                </label>
                <button
                  type="button"
                  onClick={() => onResetPassword?.()}
                  className="hover:underline text-violet-600 transition-colors"
                >
                  Reset password
                </button>
              </div>

              <button
                type="submit"
                className="animate-fade-in-delay-2 w-full rounded-2xl bg-gray-900 py-4 font-medium text-white hover:bg-gray-800 transition-colors"
              >
                Sign In
              </button>
            </form>

            <div className="animate-fade-in-delay-2 relative flex items-center justify-center">
              <span className="w-full border-t border-gray-300"></span>
              <span className="px-4 text-sm text-gray-600 bg-gray-50 absolute">Or continue with</span>
            </div>

            <button
              onClick={onGoogleSignIn}
              className="animate-fade-in-delay-2 w-full flex items-center justify-center gap-3 border border-gray-300 rounded-2xl py-4 hover:bg-gray-100 transition-colors"
            >
              <GoogleIcon />
              Continue with Google
            </button>

            <p className="animate-fade-in-delay-2 text-center text-sm text-gray-600">
              New to our platform? <button
                type="button"
                onClick={() => onCreateAccount?.()}
                className="text-violet-600 hover:underline transition-colors"
              >
                Create Account
              </button>
            </p>

            {showSellerLink && (
              <div className="animate-fade-in-delay-2 mt-2 text-center">
                <p className="text-sm text-gray-500 mb-2">Looking to grow your business?</p>
                <button
                  type="button"
                  onClick={() => onSellerLinkClick?.()}
                  className="inline-block text-base font-bold text-gray-900 hover:text-violet-600 transition-colors"
                >
                  Are you a seller? Join our marketplace →
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Right column: hero image + testimonials */}
      {heroImageSrc && (
        <section className="hidden md:block flex-1 relative p-4">
          <div
            className="animate-slide-right absolute inset-4 rounded-3xl bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImageSrc})` }}
          ></div>
          {testimonials.length > 0 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4 px-8 w-full justify-center">
              <TestimonialCard testimonial={testimonials[0]} delay="animate-fade-in-delay-2" />
              {testimonials[1] && (
                <div className="hidden xl:flex">
                  <TestimonialCard testimonial={testimonials[1]} delay="animate-fade-in-delay-2" />
                </div>
              )}
              {testimonials[2] && (
                <div className="hidden 2xl:flex">
                  <TestimonialCard testimonial={testimonials[2]} delay="animate-fade-in-delay-2" />
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default SignInComponent;